import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ClipboardCheck, CreditCard, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card } from '../../ui/card';
import { Input } from '../../ui/input';

const PLAN_LABELS = {
  community_monthly: 'Community Membership — Monthly ($50)',
  community_annual: 'Community Membership — Annual ($500)',
  founding40_monthly_legacy: 'F40 Community Membership — Monthly ($30)',
};

const STATUS_META = {
  awaiting_invoice: { label: 'Awaiting invoice', className: 'border-amber-500/20 bg-amber-500/10 text-amber-300' },
  invoice_issued: { label: 'Invoice issued', className: 'border-cyan-500/20 bg-cyan-500/10 text-cyan-300' },
  paid_reconciled: { label: 'Paid and reconciled', className: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300' },
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

const detailFrom = async (response, fallback) => {
  const data = await response.json().catch(() => ({}));
  return data.detail || fallback;
};

export default function MembershipEnrollmentsTab({ adminFetch }) {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [invoiceForms, setInvoiceForms] = useState({});
  const [paymentConfirmed, setPaymentConfirmed] = useState({});
  const [offerUserId, setOfferUserId] = useState('');
  const [offerPlanCode, setOfferPlanCode] = useState('community_monthly');
  const [f40Approved, setF40Approved] = useState(false);
  const [creatingOffer, setCreatingOffer] = useState(false);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const response = await adminFetch('/admin/membership-enrollments');
      if (!response.ok) throw new Error(await detailFrom(response, 'Could not load membership enrollments.'));
      const data = await response.json();
      setEnrollments(data.enrollments || []);
    } catch (error) {
      toast.error(error.message || 'Could not load membership enrollments.');
    } finally {
      setLoading(false);
    }
  }, [adminFetch]);

  useEffect(() => { refresh(); }, [refresh]);

  const stats = useMemo(() => ({
    awaiting: enrollments.filter((item) => item.status === 'awaiting_invoice').length,
    issued: enrollments.filter((item) => item.status === 'invoice_issued').length,
    reconciled: enrollments.filter((item) => item.status === 'paid_reconciled').length,
  }), [enrollments]);

  const updateInvoiceForm = (enrollmentId, key, value) => {
    setInvoiceForms((current) => ({
      ...current,
      [enrollmentId]: { ...(current[enrollmentId] || {}), [key]: value },
    }));
  };

  const offerPaidMembership = async () => {
    const userId = offerUserId.trim();
    if (!userId) {
      toast.error('Enter the Dad Partners account ID for the approved paid member.');
      return;
    }

    if (offerPlanCode === 'founding40_monthly_legacy' && !f40Approved) {
      toast.error('Confirm explicit F40 approval before offering the F40 plan.');
      return;
    }

    setCreatingOffer(true);
    try {
      const response = await adminFetch('/admin/membership-enrollments/offer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          plan_code: offerPlanCode,
          f40_approved: offerPlanCode === 'founding40_monthly_legacy' && f40Approved,
        }),
      });
      if (!response.ok) throw new Error(await detailFrom(response, 'Could not offer paid membership.'));
      toast.success('Paid membership offer created for the selected account.');
      setOfferUserId('');
      setOfferPlanCode('community_monthly');
      setF40Approved(false);
      await refresh({ silent: true });
    } catch (error) {
      toast.error(error.message || 'Could not offer paid membership.');
    } finally {
      setCreatingOffer(false);
    }
  };

  const linkInvoice = async (enrollment) => {
    const form = invoiceForms[enrollment.enrollment_id] || {};
    if (!form.provider_invoice_id?.trim()) {
      toast.error('Enter the Invoicer invoice number or stable invoice ID first.');
      return;
    }

    setSavingId(enrollment.enrollment_id);
    try {
      const response = await adminFetch(`/admin/membership-enrollments/${encodeURIComponent(enrollment.enrollment_id)}/link-invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!response.ok) throw new Error(await detailFrom(response, 'Could not link this invoice.'));
      toast.success('Invoice linked to this account request.');
      await refresh({ silent: true });
    } catch (error) {
      toast.error(error.message || 'Could not link this invoice.');
    } finally {
      setSavingId(null);
    }
  };

  const confirmPaymentAndReconcile = async (enrollment) => {
    if (!paymentConfirmed[enrollment.enrollment_id]) {
      toast.error('Confirm that the invoice is paid in Invoicer before reconciling access.');
      return;
    }

    setSavingId(enrollment.enrollment_id);
    try {
      const reconciliation = await adminFetch(`/admin/membership-enrollments/${encodeURIComponent(enrollment.enrollment_id)}/reconcile-paid`, { method: 'POST' });
      if (!reconciliation.ok) throw new Error(await detailFrom(reconciliation, 'Could not record the confirmed membership payment.'));

      toast.success('Payment recorded and membership enrollment reconciled.');
      await refresh({ silent: true });
    } catch (error) {
      toast.error(error.message || 'Could not reconcile this membership payment.');
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-aqua" /></div>;
  }

  return (
    <div className="space-y-6">
      <Card className="border border-emerald-200 bg-gradient-to-br from-[#f1fbf6] via-white to-[#edf9f7] p-6 dark:border-emerald-500/15 dark:bg-gradient-to-br dark:from-[#102019] dark:via-[#121b18] dark:to-[#0e1714]">
        <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-emerald-700 dark:text-emerald-300">Membership enrollment queue</p>
        <h2 className="mt-3 text-2xl font-semibold text-slate-900 dark:text-white">Match each paid invoice to exactly one Dad Partners account</h2>
        <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600 dark:text-gray-300">
          This is a manual safety bridge while Invoicer cannot securely send payment events to Dad Partners. Link the unique invoice first. Only after you verify it is paid in Invoicer should you record the payment and reconcile this request. This does not change the user’s primary tier.
        </p>
      </Card>

      <Card className="border border-sky-500/20 bg-sky-500/5 p-6">
        <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-sky-700 dark:text-sky-300">Manual payment checklist</p>
        <h3 className="mt-3 text-lg font-semibold text-slate-900 dark:text-white">Complete these steps in order for every paid membership</h3>
        <ol className="mt-4 grid gap-3 text-sm leading-6 text-slate-700 dark:text-gray-200 md:grid-cols-2">
          <li><span className="mr-2 font-semibold text-sky-700 dark:text-sky-300">1.</span>Confirm the Dad Partners account and selected plan are correct.</li>
          <li><span className="mr-2 font-semibold text-sky-700 dark:text-sky-300">2.</span>Create one Invoicer recurring invoice for that account’s exact email, plan price, and billing schedule.</li>
          <li><span className="mr-2 font-semibold text-sky-700 dark:text-sky-300">3.</span>Copy its unique invoice number or stable ID here and select <strong>Link invoice to account</strong>.</li>
          <li><span className="mr-2 font-semibold text-sky-700 dark:text-sky-300">4.</span>In Invoicer, open that exact invoice and verify its status is <strong>Paid</strong>.</li>
          <li><span className="mr-2 font-semibold text-sky-700 dark:text-sky-300">5.</span>Only then check the confirmation box and select <strong>Record payment and reconcile</strong>.</li>
          <li><span className="mr-2 font-semibold text-sky-700 dark:text-sky-300">6.</span>For renewals, failures, refunds, or cancellations, verify the exact Invoicer record before making any membership decision.</li>
        </ol>
        <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-gray-300">Never reconcile from an invoice email, a payment promise, or an invoice that belongs to a different account. “Sent” and “Pending” are not “Paid.”</p>
      </Card>

      <Card className="border border-amber-500/20 bg-amber-500/5 p-5">
        <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-amber-700 dark:text-amber-300">Offer paid membership</p>
        <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-gray-200">
          Create an account-linked membership offer before creating the matching Invoicer invoice. This does not send an invoice, collect payment, change access, or grant an Admin role.
        </p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <Input
            aria-label="Paid membership account ID"
            value={offerUserId}
            onChange={(event) => setOfferUserId(event.target.value)}
            placeholder="Dad Partners account ID"
            className="border-[#2a2a32] bg-[#1e1e24] text-white"
          />
          <select aria-label="Paid membership plan" value={offerPlanCode} onChange={(event) => { setOfferPlanCode(event.target.value); setF40Approved(false); }} className="rounded-md border border-[#2a2a32] bg-[#1e1e24] px-3 text-white">
            <option value="community_monthly">Standard Member — Monthly ($50)</option>
            <option value="community_annual">Standard Member — Annual ($500)</option>
            <option value="founding40_monthly_legacy">F40 Community Membership — Monthly ($30)</option>
          </select>
          <Button onClick={offerPaidMembership} disabled={creatingOffer || (offerPlanCode === 'founding40_monthly_legacy' && !f40Approved)} className="bg-amber-400 text-slate-950 hover:bg-amber-300">
            {creatingOffer ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ClipboardCheck className="mr-2 h-4 w-4" />} Offer paid membership
          </Button>
        </div>
        {offerPlanCode === 'founding40_monthly_legacy' && (
          <label className="mt-4 flex cursor-pointer items-start gap-3 text-sm text-slate-700 dark:text-gray-200">
            <input aria-label="Confirm F40 approval" type="checkbox" checked={f40Approved} onChange={(event) => setF40Approved(event.target.checked)} className="mt-1 h-4 w-4 accent-cyan-400" />
            <span>I specifically approved this F40 $30/month offer.</span>
          </label>
        )}
      </Card>

      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="grid grid-cols-3 gap-3 text-center">
          {[
            ['Awaiting invoice', stats.awaiting, 'text-amber-300'],
            ['Invoice issued', stats.issued, 'text-cyan-300'],
            ['Reconciled', stats.reconciled, 'text-emerald-300'],
          ].map(([label, value, color]) => (
            <Card key={label} className="min-w-28 border-[#2a2a32] bg-[#1e1e24] px-4 py-3">
              <p className="text-xs text-gray-400">{label}</p><p className={`mt-1 text-2xl font-bold ${color}`}>{value}</p>
            </Card>
          ))}
        </div>
        <Button onClick={() => refresh({ silent: true })} variant="outline" className="border-[#2a2a32] text-white hover:bg-[#16161a]">
          <RefreshCw className="mr-2 h-4 w-4" /> Refresh queue
        </Button>
      </div>

      {!enrollments.length ? (
        <Card className="border-[#2a2a32] bg-[#1e1e24] p-10 text-center text-gray-400">No membership enrollment requests yet.</Card>
      ) : (
        <div className="space-y-4">
          {enrollments.map((enrollment) => {
            const meta = STATUS_META[enrollment.status] || STATUS_META.awaiting_invoice;
            const form = invoiceForms[enrollment.enrollment_id] || {};
            const isSaving = savingId === enrollment.enrollment_id;
            return (
              <Card key={enrollment.enrollment_id} className="border-[#2a2a32] bg-[#1e1e24] p-6">
                <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                  <div className="min-w-0 space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-semibold text-white">{enrollment.name_snapshot || 'Dad Partners account'}</p>
                      <Badge className={meta.className}>{meta.label}</Badge>
                    </div>
                    <p className="text-sm text-gray-400">{enrollment.email_snapshot || 'No email snapshot available'}</p>
                    <div className="flex flex-wrap gap-2 text-xs">
                      <Badge className="border-slate-500/20 bg-slate-500/10 text-slate-300">{PLAN_LABELS[enrollment.plan_code] || enrollment.plan_code}</Badge>
                      <Badge className="border-slate-500/20 bg-slate-500/10 text-slate-300">Requested {formatDate(enrollment.requested_at)}</Badge>
                      <Badge className="border-slate-500/20 bg-slate-500/10 text-slate-300">Account ID {enrollment.user_id}</Badge>
                    </div>
                  </div>

                  <div className="w-full max-w-xl">
                    {enrollment.status === 'awaiting_invoice' && (
                      <div className="rounded-xl bg-[#16161a] p-4">
                        <div className="flex items-start gap-2"><CreditCard className="mt-0.5 h-5 w-5 text-aqua" /><p className="text-sm font-medium text-white">Create a unique invoice in Invoicer, addressed to this exact account email, then record its stable number here.</p></div>
                        <div className="mt-4 grid gap-3 sm:grid-cols-2">
                          <Input aria-label="Invoicer invoice ID" value={form.provider_invoice_id || ''} onChange={(event) => updateInvoiceForm(enrollment.enrollment_id, 'provider_invoice_id', event.target.value)} placeholder="Invoicer invoice number / ID" className="border-[#2a2a32] bg-[#1e1e24] text-white" />
                          <Input value={form.provider_customer_id || ''} onChange={(event) => updateInvoiceForm(enrollment.enrollment_id, 'provider_customer_id', event.target.value)} placeholder="Invoicer customer ID (optional)" className="border-[#2a2a32] bg-[#1e1e24] text-white" />
                        </div>
                        <Input value={form.provider_subscription_id || ''} onChange={(event) => updateInvoiceForm(enrollment.enrollment_id, 'provider_subscription_id', event.target.value)} placeholder="Invoicer subscription ID (optional)" className="mt-3 border-[#2a2a32] bg-[#1e1e24] text-white" />
                        <Button onClick={() => linkInvoice(enrollment)} disabled={isSaving} className="mt-4 bg-aqua text-charcoal hover:bg-aqua-dark">
                          {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ExternalLink className="mr-2 h-4 w-4" />} Link invoice to account
                        </Button>
                      </div>
                    )}

                    {enrollment.status === 'invoice_issued' && (
                      <div className="rounded-xl bg-[#16161a] p-4">
                        <p className="text-sm font-medium text-white">Linked invoice: <span className="text-aqua">{enrollment.provider_invoice_id}</span></p>
                        <p className="mt-2 text-sm leading-6 text-gray-400">Open Invoicer and verify this exact invoice is paid before continuing. Do not activate based on an email, a screenshot, or a payment promise.</p>
                        <label className="mt-4 flex cursor-pointer items-start gap-3 text-sm text-gray-200">
                          <input type="checkbox" checked={Boolean(paymentConfirmed[enrollment.enrollment_id])} onChange={(event) => setPaymentConfirmed((current) => ({ ...current, [enrollment.enrollment_id]: event.target.checked }))} className="mt-1 h-4 w-4 accent-cyan-400" />
                          <span>I verified this invoice is paid in Invoicer and it belongs to this account.</span>
                        </label>
                        <Button onClick={() => confirmPaymentAndReconcile(enrollment)} disabled={isSaving || !paymentConfirmed[enrollment.enrollment_id]} className="mt-4 bg-emerald-400 text-slate-950 hover:bg-emerald-300">
                          {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ClipboardCheck className="mr-2 h-4 w-4" />} Record payment and reconcile
                        </Button>
                      </div>
                    )}

                    {enrollment.status === 'paid_reconciled' && (
                      <div className="flex items-center gap-2 rounded-xl bg-emerald-500/10 p-4 text-sm text-emerald-200"><CheckCircle2 className="h-5 w-5" /> Reconciled {formatDate(enrollment.reconciled_at)}. The invoice-to-account audit record is complete.</div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
